import type { ComponentPropsWithoutRef } from 'react'
import cc from 'classcat'

export type ButtonVariant = 'primary' | 'outline' | 'danger' | 'link' | 'circular' | 'unstyled'
export type ButtonIcon = 'add' | 'edit' | 'crop' | 'change' | 'delete' | 'reset' | 'replay'

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  readonly variant?: ButtonVariant
  readonly icon?: ButtonIcon
  readonly size?: 'small'
  readonly fullWidth?: boolean
}

/*
 * Editor button component. Covers all editor button variants including circular icon buttons.
 *
 * variant="primary"  — filled CTA button (default)
 * variant="outline"  — secondary/cancel button
 * variant="danger"   — destructive action button
 * variant="link"     — transparent link-styled button; combine with icon="replay" or icon="reset"
 * variant="circular" — circular icon-only button; requires the `icon` prop
 * variant="unstyled" — strips browser defaults for fully custom-styled semantic buttons
 *
 * icon               — optional on any variant, required on "circular"
 * size="small"       — smaller padding/font (not applicable to circular)
 * fullWidth          — makes the button take the full width of its parent (not applicable to circular)
 */
export default function Button({
  variant = 'primary',
  icon,
  size,
  fullWidth,
  className,
  disabled,
  children,
  title,
  'aria-label': ariaLabel,
  ...rest
}: ButtonProps) {
  const isCircular = variant === 'circular'

  // For icon-only circular buttons, fall back to title as the accessible name
  const computedAriaLabel = ariaLabel ?? (isCircular && !children ? title : undefined)

  return (
    <button
      {...rest}
      title={title}
      aria-label={computedAriaLabel}
      disabled={disabled}
      className={cc([
        className,
        isCircular
          ? {
              'ep-button-circular': true,
              [`ep-button-icon-${icon}`]: Boolean(icon),
            }
          : {
              'ep-button': true,
              [`ep-button-${variant}`]: true,
              'ep-button-small': size === 'small',
              'ep-button-full-width': fullWidth,
              [`ep-button-icon-${icon}`]: Boolean(icon),
            },
      ])}
    >
      {children}
    </button>
  )
}
